import { h, State, Component, Event, EventEmitter, Prop, Host } from '@stencil/core';
import state from '../../state';
import {
  transitionScreen,
  getViewportValue,
  updateStoredField,
  getStoredFormData,
  clearFormStorage,
} from '../../utils';
import { SIGNUP_URL } from '../../constants';

import type { EmailContent, OpenTrackingConsentValue, SubmitEventArg } from '../../types';

@Component({
  tag: 'om-email-screen',
  styleUrl: './screens.css',
})
export class EmailScreen {
  @Prop() screenValues: EmailContent;
  @Prop() handleSubmit: (d: SubmitEventArg) => void;

  @State() emailErrorMsg: string | undefined;
  @State() consentChecked: boolean = false;

  @Event() layoutContainerClicked: EventEmitter;
  submitEvent: EventEmitter<SubmitEventArg>;
  emailInput: HTMLInputElement;
  private hasInitialized = false;

  private submitEventHandler(identifier: string) {
    if (state.mode !== 'live') {
      return;
    }

    this.handleSubmit({ channel: 'email', identifier });
  }

  // 'nobody' is never shown; 'countries_requiring_consent' depends on the
  // server-resolved flag, which is undefined until the API/loader actually
  // populates it - undefined resolves to hidden, the safe default. In preview
  // mode there's no real visitor to resolve a country for, so the checkbox is
  // always shown for 'countries_requiring_consent' so a merchant can see it.
  private isOpenTrackingConsentVisible(consent?: OpenTrackingConsentValue): boolean {
    if (!consent) return false;
    if (consent.mode === 'countries_requiring_consent') {
      return state.mode === 'preview' || state.usersCountryRequiresOpenTrackingConsent === true;
    }
    return false;
  }

  private async handleEmailSubmit(event: SubmitEvent) {
    event.preventDefault();
    state.showErrors = false;
    this.emailErrorMsg = undefined;
    //only run if mode is live or test
    if (state.mode !== 'preview') {
      // validate email input
      const { validity } = this.emailInput;
      if (!validity.valid) {
        this.emailErrorMsg = 'Please input a valid email address';

        if (validity.valueMissing) {
          this.emailErrorMsg = 'Email field is mandatory to be able to submit the form';
        }

        if (validity.tooLong) {
          this.emailErrorMsg = 'Email address is too long';
        }

        return;
      }
      if (state.customFieldErrorMsg && Object.keys(state.customFieldErrorMsg)?.length) {
        state.showErrors = true;
        return;
      }
      // on success, change currentScreen to success or sms
      if (state.mode === 'test') {
        if (state.miniOverlayConfig && !state.miniOverlayConfig.shouldHide) {
          clearFormStorage();
        }

        transitionScreen(state.content.sms.shouldHide ? 'success' : 'sms');

        this.emailErrorMsg = undefined;
        return;
      }

      if (state.mode === 'live') {
        const form = new FormData((event?.submitter as HTMLFormElement)?.form);

        const ue = form.get('ue');
        const email = ue instanceof File || ue === null ? undefined : ue;

        const consentValue = this.screenValues.open_tracking_consent
          ? getViewportValue(this.screenValues.open_tracking_consent)
          : undefined;
        if (this.isOpenTrackingConsentVisible(consentValue)) {
          form.set('open_tracking_consent', this.consentChecked ? 'true' : 'false');
        }

        this.emailErrorMsg = undefined;
        try {
          const response = await fetch(SIGNUP_URL, {
            body: form,
            method: 'post',
          });

          if (response.status !== 202) {
            // Response is not ok, handle the error
            const errorData = await response.json();
            throw errorData;
          }

          await response.json();
          this.submitEventHandler(email);
          if (state.miniOverlayConfig && !state.miniOverlayConfig.shouldHide) {
            clearFormStorage();
          }
          transitionScreen(state.content.sms.shouldHide ? 'success' : 'sms');
        } catch (err) {
          if (err?.errors && Object.keys(err.errors).length) {
            state.customFieldErrorMsg = {
              ...state.customFieldErrorMsg,
              ...err.errors,
            };
            state.showErrors = true;
          }
        }
      }
    }
  }

  componentDidRender() {
    // Only run initialization logic on first render
    if (!this.hasInitialized) {
      this.hasInitialized = true;

      // Then focus the email input
      if (state.mode !== 'preview' && this.emailInput) {
        setTimeout(() => {
          this.emailInput?.focus();
        }, 100);
      }
    }
  }

  render() {
    const { placeholder, offset } = getViewportValue(this.screenValues.input);
    const [isAbsolute, offsetVal] = offset ?? [false, '0'];
    const customFields = this.screenValues.customFields ?? [];
    const isAllHiddenCustomFields = customFields.every(field => field.type === 'hidden');

    const consentValue = this.screenValues.open_tracking_consent
      ? getViewportValue(this.screenValues.open_tracking_consent)
      : undefined;
    const showConsent = this.isOpenTrackingConsentVisible(consentValue);

    // Get stored email value for restoration (only if mini overlay is enabled)
    let storedEmail = '';
    if (state.miniOverlayConfig && !state.miniOverlayConfig.shouldHide) {
      const storedData = getStoredFormData('email');
      if (storedData?.email) {
        storedEmail = storedData.email;
        // Also update state.emailValue so it's in sync
        if (!state.emailValue) {
          state.emailValue = storedEmail;
        }
      }
    }

    return (
      <Host
        style={{
          ['--textBlockBgColour']: getViewportValue(this.screenValues.textBlockBgColour, 'colour'),
          ['--emailInputOffset']: `${offsetVal ?? '0'}px`,
        }}
      >
        <div
          class={state.isMobileViewport ? 'screen-container is-mobile-viewport' : 'screen-container'}
          onClick={this.layoutContainerClicked.emit}
          ref={(el: HTMLDivElement) => {
            state.currentScreenContainer = el as HTMLElement;
          }}
        >
          <div class="content-area">
            <div class={`content-inner${isAbsolute ? ' absolute-input' : ''}`}>
              <div class="copy-container">
                <om-clickable-element
                  innerHTML={getViewportValue(this.screenValues.title)}
                  class="title"
                  element={{ field: 'title' }}
                />
                <om-clickable-element
                  innerHTML={getViewportValue(this.screenValues.body)}
                  class="body"
                  element={{ field: 'body' }}
                />
              </div>
              <div class="bottom-section">
                <form
                  novalidate
                  onSubmit={(e: SubmitEvent) => {
                    this.handleEmailSubmit(e);
                  }}
                  class="form"
                >
                  <input type="hidden" name="__overlay_id" value={state.overlayId} />
                  <input type="hidden" name="email" autocomplete="off" />
                  <div class="hide">
                    {' '}
                    <input type="email" name="__email" autocomplete="off" />{' '}
                  </div>
                  <input type="hidden" name="@account" value={state.account} />
                  <input type="hidden" name="@subscription_status" value="SUBSCRIBED" />
                  <div
                    class={`inputs-container${customFields?.length ? ' has-custom-fields' : ''}${isAllHiddenCustomFields ? ' cta-next-to-email' : ''}`}
                  >
                    <om-clickable-element class="input-container" element={{ field: 'input' as keyof EmailContent }}>
                      <input
                        tabindex="0"
                        aria-label="Email address"
                        {...(state.mode === 'preview' ? { inert: true } : { autoFocus: true })}
                        required
                        maxLength={255}
                        ref={el => (this.emailInput = el)}
                        id="om_overlay_email_input"
                        type="email"
                        name="ue"
                        placeholder={placeholder}
                        class="input"
                        {...(storedEmail && { value: storedEmail })}
                        onChange={e => {
                          const input = e.target as HTMLInputElement;
                          state.emailValue = input.value;
                        }}
                        onBlur={e => {
                          if (
                            state.mode !== 'preview' &&
                            state.miniOverlayConfig &&
                            !state.miniOverlayConfig.shouldHide
                          ) {
                            const input = e.target as HTMLInputElement;
                            updateStoredField('email', 'email', input.value);
                          }
                        }}
                      />
                    </om-clickable-element>
                    {!!customFields?.length && (
                      <om-custom-fields customFields={this.screenValues.customFields} screen="email" />
                    )}
                    <om-clickable-element class="cta-wrapper" element={{ field: 'cta' }}>
                      <button
                        type="submit"
                        class="cta"
                        innerHTML={getViewportValue(this.screenValues.cta, 'text')}
                      ></button>
                    </om-clickable-element>
                    <span id="om-overlays-error-message" class={this.emailErrorMsg ? 'error' : 'hide'}>
                      {this.emailErrorMsg}
                    </span>
                  </div>
                </form>
                {showConsent && (
                  <om-clickable-element
                    class="open-tracking-consent-wrapper"
                    element={{ field: 'open_tracking_consent' }}
                  >
                    <label class="open-tracking-consent-label" {...(state.mode === 'preview' ? { inert: true } : {})}>
                      <input
                        type="checkbox"
                        checked={this.consentChecked}
                        onChange={e => {
                          this.consentChecked = (e.target as HTMLInputElement).checked;
                        }}
                      />
                      <span innerHTML={consentValue?.label} />
                    </label>
                  </om-clickable-element>
                )}
              </div>
            </div>
            <div class="policy-section">
              <om-clickable-element
                innerHTML={getViewportValue(this.screenValues.privacyPolicy)}
                class="privacy-policy-container"
                element={{ field: 'privacyPolicy' }}
              />
            </div>
          </div>
        </div>
      </Host>
    );
  }
}
